import { BusStop, IBusStop, IBusStopData } from "@/domain/entities/busStop";
import {
  FavoriteLine,
  IFavoriteLine,
  IFavoriteLineData,
} from "@/domain/entities/favoriteLine";
import { ParseError } from "@/repositories/shared/errors/parseError";
import {
  BookIntervalCode,
  LineTypeCode,
  WeekdayCode,
  WeekdayCodeName,
} from "@/types";
import useGetLineTypeName from "@/use/useGetLineTypeName";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import {
  HttpBookInterval,
  HttpBusStop,
  HttpEarlyDriveInfo,
  HttpFavoriteLine,
  HttpLineSet,
  IFavoriteLineParser,
} from "../lineInfoRepository.types";
import {
  IBookInterval,
  IEarlyDriveData,
  ILineSet,
} from "@/domain/entities/lineInfo";
import { parseBookInterval } from "./bookIntervalParser";
import { getLineFare, parseBus, sortBuses } from "./lineInfoParser";
import dayjs from "dayjs";

function parseBusStopList(
  stations: Array<HttpBusStop>,
  onStationUid: string
): IBusStop[] {
  const busStops: IBusStop[] = [];

  // 화면 표시 안하는 정류장 필터링
  const filteredStations = stations?.filter((station) => station.displayYn);

  filteredStations?.map((station: HttpBusStop, index: number) => {
    const busStopData: IBusStopData = {
      id: station.uid,
      number: station.no,
      name: station.name,
      shortName: station.shortName,
      order: index,
      departureTime: station.departureTime?.trim(),
      arrivalTime: station.arrivalTime?.trim(),
      isFirstStop: station.beginYn,
      isLastStop: station.endYn,
      isGetOnStop: station.onYn,
      isGetOffStop: station.offYn,
      isTransitStop: station.transYn,
      isReturnStop: station.returnYn,
      isDisplayStop: station.displayYn,
      isOmitted: station.omittedYn,
      isTemp: station.tempYn,
      shouldShowStreetView: station.streetViewYn,
      shouldShowBusStopLayout: station.useStationLayoutYn,
      latitude: station.lat,
      longitude: station.lng,
      direction: "",
      isMyBoardingStop: station.uid === onStationUid,
      transferBusStops: station.trans?.routes,
      transferGuide: station.trans?.transText,
      isFavorite: station.isFavorite ?? false,
      useBeginDtm: dayjs(station.useBeginDtm).format("YYYY-MM-DD HH:mm"),
      useEndDtm: station.useEndDtm
        ? dayjs(station.useEndDtm).format("YYYY-MM-DD HH:mm")
        : undefined,
    };

    busStops.push(new BusStop(busStopData));
  });
  return busStops;
}

function findBusStopById(id: string, busStops: Array<IBusStop>) {
  return busStops.find((busStop) => busStop.id === id);
}

function getBookIntervalWithCode(
  data: HttpBookInterval[] | undefined,
  code: BookIntervalCode
): HttpBookInterval | undefined {
  return data?.find((bookInterval) => bookInterval.bookIntervalCode === code);
}

function parseBookIntervalWithCode(
  data: HttpBookInterval[] | undefined,
  code: BookIntervalCode
): IBookInterval | undefined {
  const httpBookInterval = getBookIntervalWithCode(data, code);
  if (!httpBookInterval) return undefined;
  return parseBookInterval(httpBookInterval);
}

function convertWeekdayName(weekdayCode: string): WeekdayCodeName {
  const weekdayCodeMap: Record<string, WeekdayCodeName> = {
    [WeekdayCode.MON]: WeekdayCodeName.MON,
    [WeekdayCode.TUE]: WeekdayCodeName.TUE,
    [WeekdayCode.WED]: WeekdayCodeName.WED,
    [WeekdayCode.THU]: WeekdayCodeName.THU,
    [WeekdayCode.FRI]: WeekdayCodeName.FRI,
    [WeekdayCode.SAT]: WeekdayCodeName.SAT,
    [WeekdayCode.SUN]: WeekdayCodeName.SUN,
    [WeekdayCode.HOLI]: WeekdayCodeName.HOLI,
  };

  return weekdayCodeMap[weekdayCode] ?? WeekdayCodeName.NONE;
}

function sortByWeekday(earlyDriveInfos: IEarlyDriveData[]): IEarlyDriveData[] {
  // 요일에 따라 정렬(월,화,수,목,금,토,일,휴 순)
  const sortedWeekDay = Object.values(WeekdayCode);

  const sortedEarlyDriveInfos = earlyDriveInfos?.sort((a, b) => {
    const indexOfA = sortedWeekDay.findIndex(
      (weekday) => weekday === a.weekdayCode
    );
    const indexOfB = sortedWeekDay.findIndex(
      (weekday) => weekday === b.weekdayCode
    );
    return indexOfA - indexOfB;
  });

  return sortedEarlyDriveInfos;
}

function getEarlyDriveInfos(
  earlyDriveInfos: HttpEarlyDriveInfo[] | undefined
): IEarlyDriveData[] | undefined {
  if (!earlyDriveInfos) return;

  const parsedEarlyDriveInfos = earlyDriveInfos?.map((earlyDriveInfo) => {
    return {
      weekdayCode: earlyDriveInfo.weekdayCode,
      weekdayName: convertWeekdayName(earlyDriveInfo.weekdayCode),
      earlyMinute: earlyDriveInfo.earlyMinute,
    };
  });

  const sortedEarlyDriveInfos = sortByWeekday(parsedEarlyDriveInfos);

  return sortedEarlyDriveInfos;
}

function parseLineSet(lineSet: HttpLineSet): ILineSet {
  return {
    lineSetCodeUid: lineSet.lineSetCodeuid,
    name: lineSet.name,
    displayText: lineSet.shortName ? lineSet.shortName : lineSet.name,
    isToday: lineSet.todayYn === "Y",
  };
}

@injectable()
export class FavoriteLineParser implements IFavoriteLineParser {
  constructor() {}

  toDomain(data: HttpFavoriteLine): Result<IFavoriteLine, ParseError> {
    try {
      const busStops = parseBusStopList(
        data.stations ?? [],
        data.onStation?.uid ?? ""
      );
      const buses = data.buses?.map(parseBus);
      const favoriteLineData: IFavoriteLineData = {
        lineId: data.route?.uid ?? "",
        turnId: data.uid,
        lineNum: data.route?.no ?? "",
        plateNum: "",
        lineStartTime: data.stations?.[0]?.departureTime?.trim() ?? "",
        routeName: data.route?.name ?? "",
        lineTypeCode: data.route?.lineTypeCode ?? LineTypeCode.Forward,
        lineTypeName: useGetLineTypeName(
          data.route?.lineTypeCode ?? LineTypeCode.Forward
        ),
        busStops: busStops,
        getOnBusStop: findBusStopById(data.onStation?.uid ?? "", busStops),
        getOffBusStop: findBusStopById(data.offStation?.uid ?? "", busStops),
        firstBusStop: busStops[0],
        lastBusStop: busStops[busStops.length - 1],
        getOnNotice: data.onNoticeYn,
        getOffNotice: data.offNoticeYn,
        lineFare: getLineFare(data.fares, data.uid),
        bookIntervals: data.bookIntervals?.map(parseBookInterval),
        earlyDriveInfos: getEarlyDriveInfos(data.earlyDriveWeekly),
        lineSets: data.lineSets?.map(parseLineSet) ?? [],
        buses: buses ? sortBuses(buses) : undefined,
        gateNoList: data.gateNoList,
        monthlyBookChecked: false, // 메인화면 즐겨찾기 카드에서 월예약 체크박스 선택 값으로 사용
        isBoarding: false, // 메인화면 즐겨찾기 카드에서 탑승중인지 여부 값으로 사용
        detourBeginDtm: data.detourBeginDtm,
        detourEndDtm: data.detourEndDtm,
        detourReason: data.detourReason,
      };
      const favoriteLine = new FavoriteLine(favoriteLineData);
      return ok(favoriteLine);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
